import React, { useState } from "react";
import { Button, Tag } from "../../../../../design-system";
import mediaOps, { JobSummary, PLATFORMS } from "../../../../../database/mediaOps/mediaOpsApi";
import { useMutation, useOpRead } from "./hooks";
import { FieldLabel, FormGrid, Grow, Hint, ListRow, Panel, PanelHead, ResultNotice, Row, Select, Small, TextInput } from "./ui";

// Trend evidence. A metric that was not observed is "unknown", never zero.
// Automated scans require a finite budget and a ready worker. Manual entry
// remains available when a provider is unavailable.

const METRICS = ["views", "likes", "comments", "shares"] as const;

export const metricText = (v: unknown): string => (typeof v === "number" ? v.toLocaleString() : "unknown");

type Evidence = {
  evidenceId: string;
  platform: string;
  sourceUrl: string;
  creator?: string | null;
  ageDays?: number;
  publishedAt?: string | null;
  publicationFreshness?: string;
  lastObservedAt?: string;
  metrics?: Record<string, number | null>;
  nicheTags?: string[];
  hook?: string | null;
  confidence?: string;
  retrievalMethod?: string;
};

export const TrendsView: React.FC<{ communityId: string }> = ({ communityId }) => {
  const [freshness, setFreshness] = useState("14");
  const [niche, setNiche] = useState("");
  const read = useOpRead(
    () => mediaOps.listTrends(communityId, { freshnessDays: Number(freshness) || 14, ...(niche.trim() ? { niche: niche.trim() } : {}) }),
    [communityId, freshness, niche]
  );
  const data = read.result?.data || {};
  const list: Evidence[] = data.evidence || [];

  return (
    <>
      <TrendScanForm communityId={communityId} onSaved={read.reload} />
      <Panel>
        <PanelHead>
          Trend observations <Grow />
          <Button size="sm" variant="secondary" onClick={read.reload}>
            Refresh
          </Button>
        </PanelHead>
        <FormGrid>
          <FieldLabel>
            Freshness in days
            <TextInput type="number" min={1} max={365} value={freshness} onChange={(e) => setFreshness(e.target.value)} />
          </FieldLabel>
          <FieldLabel>
            Niche tag
            <TextInput value={niche} onChange={(e) => setNiche(e.target.value)} />
          </FieldLabel>
        </FormGrid>
        {read.result && read.result.status === "blocked" ? <ResultNotice result={read.result} /> : null}
        {read.result && read.result.warnings.length > 0 ? <ResultNotice result={{ ...read.result, status: "completed", errors: [] }} okText="Notes" /> : null}
        {typeof data.staleSkipped === "number" && data.staleSkipped > 0 ? <Small>{data.staleSkipped} older items are hidden by the freshness window.</Small> : null}
        {list.length === 0 && read.result?.status !== "blocked" ? <Small>No recently observed evidence.</Small> : null}
        {list.map((e) => (
          <ListRow key={e.evidenceId} style={{ flexDirection: "column", alignItems: "stretch" }}>
            <Row>
              <Tag size="sm">{e.platform}</Tag>
              <a href={e.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>
                {e.creator || e.sourceUrl}
              </a>
              <Grow />
              <Small>
                Seen {typeof e.ageDays === "number" ? `${e.ageDays} days ago` : "at an unknown time"}. Confidence {e.confidence || "unknown"}.{" "}
                {e.retrievalMethod}
              </Small>
            </Row>
            <Row>
              {METRICS.map((k) => (
                <Small key={k}>
                  {k}: {metricText(e.metrics?.[k])}
                </Small>
              ))}
            </Row>
            {e.hook ? <Small>Hook: {e.hook}</Small> : null}
            <Small>Published {e.publishedAt || "unknown"} ({e.publicationFreshness || "unknown freshness"}). Source evidence is inspiration, not reusable media.</Small>
            {e.nicheTags && e.nicheTags.length ? <Small>Tags: {e.nicheTags.join(", ")}</Small> : null}
          </ListRow>
        ))}
      </Panel>
      <ManualEvidenceForm communityId={communityId} onSaved={read.reload} />
    </>
  );
};

const TrendScanForm: React.FC<{ communityId: string; onSaved: () => void }> = ({ communityId, onSaved }) => {
  const [source, setSource] = useState("both");
  const [terms, setTerms] = useState("");
  const [days, setDays] = useState("14");
  const [cap, setCap] = useState("30");
  const [budget, setBudget] = useState("");
  const [jobId, setJobId] = useState("");
  const mutation = useMutation<{ jobId?: string; unavailable?: { source: string; reason: string }[] }>();
  const job = useOpRead<JobSummary>(() => jobId ? mediaOps.getJob(communityId, jobId) : null, [communityId, jobId]);
  const submit = async () => {
    const queries = terms.split(/[,\n]/).map((t) => t.trim()).filter(Boolean);
    const sources = source === "both" ? ["instagram_hashtags", "tiktok_search"] : [source];
    const res = await mutation.run((k) => mediaOps.startTrendScan(k, communityId, { sources, queries, lookbackDays: Number(days), maxResults: Number(cap), budgetLimitUsd: Number(budget) }));
    if (res?.data?.jobId) setJobId(res.data.jobId);
  };
  const result = job.result?.data;
  const sourceOutcomes = (result?.diagnostics as any)?.sources || [];
  return <Panel>
    <PanelHead>Research Instagram and TikTok trends</PanelHead>
    <Hint>Enter one to five niche terms. The budget is a total cap for this job. Research never imports media or publishes a post.</Hint>
    <FormGrid>
      <FieldLabel>Source<Select value={source} onChange={(e) => { setSource(e.target.value); mutation.edited(); }}><option value="both">Instagram + TikTok</option><option value="instagram_hashtags">Instagram keywords</option><option value="tiktok_search">TikTok search</option></Select></FieldLabel>
      <FieldLabel>Terms, comma separated<TextInput value={terms} onChange={(e) => { setTerms(e.target.value); mutation.edited(); }} placeholder="digital products, AI tools" /></FieldLabel>
      <FieldLabel>Lookback days<TextInput type="number" min={1} max={90} value={days} onChange={(e) => { setDays(e.target.value); mutation.edited(); }} /></FieldLabel>
      <FieldLabel>Results per source<TextInput type="number" min={1} max={100} value={cap} onChange={(e) => { setCap(e.target.value); mutation.edited(); }} /></FieldLabel>
      <FieldLabel>Maximum spend, USD<TextInput type="number" min="0.01" step="0.01" value={budget} onChange={(e) => { setBudget(e.target.value); mutation.edited(); }} /></FieldLabel>
    </FormGrid>
    <Row><Button size="sm" onClick={submit} disabled={!terms.trim() || !(Number(budget) > 0)} isLoading={mutation.busy}>Start bounded scan</Button></Row>
    <ResultNotice result={mutation.result} okText={jobId ? `Queued job ${jobId}` : "Scan request recorded."} />
    {jobId ? <Row><Small>Job {jobId}: {result?.status || "loading"}; {result?.stage || "queued"}</Small><Button size="sm" variant="secondary" onClick={() => { job.reload(); onSaved(); }}>Refresh progress and evidence</Button></Row> : null}
    {sourceOutcomes.map((s: any) => <Small key={s.source}>{s.source}: {s.status || s.phase} {s.reason || ""}; {s.evidenceIds?.length || 0} evidence items; cost {typeof s.costUsd === "number" ? `$${s.costUsd}` : "unknown"}</Small>)}
  </Panel>;
};

const ManualEvidenceForm: React.FC<{ communityId: string; onSaved: () => void }> = ({ communityId, onSaved }) => {
  const [url, setUrl] = useState("");
  const [platform, setPlatform] = useState("tiktok");
  const [sourceId, setSourceId] = useState("");
  const [creator, setCreator] = useState("");
  const [hook, setHook] = useState("");
  const [tags, setTags] = useState("");
  const [confidence, setConfidence] = useState("medium");
  const [metrics, setMetrics] = useState<Record<string, string>>({ views: "", likes: "", comments: "", shares: "" });
  const m = useMutation();
  const edit = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    m.edited();
  };
  const submit = async () => {
    const res = await m.run((k) =>
      mediaOps.saveTrend(k, communityId, {
        observations: [
          {
            sourceUrl: url.trim(),
            platform,
            sourceId: sourceId.trim() || url.trim(),
            observedAt: new Date().toISOString(),
            metrics: Object.fromEntries(METRICS.map((k2) => [k2, metrics[k2].trim() === "" ? null : Number(metrics[k2])])),
            nicheTags: tags
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean),
            retrievalMethod: "manual",
            confidence,
            ...(creator.trim() ? { creator: creator.trim() } : {}),
            ...(hook.trim() ? { hook: hook.trim() } : {}),
          },
        ],
      })
    );
    if (res && res.status !== "blocked") {
      setUrl("");
      setSourceId("");
      setHook("");
      onSaved();
    }
  };
  return (
    <Panel>
      <PanelHead>Add evidence by hand</PanelHead>
      <Hint>Leave a metric empty when you did not see it. It is stored as unknown, not zero.</Hint>
      <FormGrid>
        <FieldLabel>
          Source URL (https)
          <TextInput value={url} onChange={(e) => edit(setUrl)(e.target.value)} />
        </FieldLabel>
        <FieldLabel>
          Platform
          <Select value={platform} onChange={(e) => edit(setPlatform)(e.target.value)}>
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel>
          Post id (optional)
          <TextInput value={sourceId} onChange={(e) => edit(setSourceId)(e.target.value)} />
        </FieldLabel>
        <FieldLabel>
          Creator (optional)
          <TextInput value={creator} onChange={(e) => edit(setCreator)(e.target.value)} />
        </FieldLabel>
        {METRICS.map((k) => (
          <FieldLabel key={k}>
            {k}
            <TextInput
              type="number"
              min={0}
              placeholder="unknown"
              value={metrics[k]}
              onChange={(e) => {
                const v = e.target.value;
                setMetrics((prev) => ({ ...prev, [k]: v }));
                m.edited();
              }}
            />
          </FieldLabel>
        ))}
        <FieldLabel>
          Hook (optional)
          <TextInput value={hook} onChange={(e) => edit(setHook)(e.target.value)} />
        </FieldLabel>
        <FieldLabel>
          Niche tags, comma separated
          <TextInput value={tags} onChange={(e) => edit(setTags)(e.target.value)} />
        </FieldLabel>
        <FieldLabel>
          Confidence
          <Select value={confidence} onChange={(e) => edit(setConfidence)(e.target.value)}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </Select>
        </FieldLabel>
      </FormGrid>
      <Row>
        <Button size="sm" onClick={submit} disabled={!/^https:\/\//i.test(url.trim())} isLoading={m.busy}>
          Save evidence
        </Button>
      </Row>
      <ResultNotice result={m.result} okText="Evidence saved." />
    </Panel>
  );
};

export default TrendsView;
